import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const SESSION_KEY = "zebo_chat_session";
const HISTORY_KEY = "zebo_chat_history";

function ensureSession() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `zc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return `zc-${Date.now()}`;
  }
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [leadCreated, setLeadCreated] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(ensureSession()).current;

  // Restore history
  useEffect(() => {
    try {
      const raw = localStorage.getItem(HISTORY_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setMessages(parsed);
      }
    } catch { /* ignore */ }
  }, []);

  // Persist history
  useEffect(() => {
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(messages.slice(-40))); } catch { /* ignore */ }
  }, [messages]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Show greeting first time it's opened
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: "Hi! I'm ZEBO Concierge. Full-home interiors, modular kitchens, wardrobes or a commercial fit-out — how can I help today?" }]);
    }
  }, [open, messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const { data } = await api.post("/chat", { session_id: sessionId, message: text });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.lead_created && !leadCreated) {
        setLeadCreated(true);
        trackEvent("chatbot_lead_created", { session_id: sessionId });
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || "Sorry, I'm offline for a moment. Please try again.";
      setMessages((m) => [...m, { role: "assistant", content: detail }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  return (
    <>
      {/* Trigger — bottom-left so it doesn't collide with the WhatsApp button */}
      <button
        data-testid="chat-widget-toggle"
        aria-label={open ? "Close chat" : "Open chat"}
        onClick={() => { setOpen((o) => !o); trackEvent("chatbot_toggle", { open: !open }); }}
        className="fixed bottom-6 left-6 z-[60] bg-[#143D63] text-[#F9F8F6] rounded-full w-14 h-14 flex items-center justify-center shadow-xl hover:bg-[#8B0000] transition-colors"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-widget-panel"
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            className="fixed bottom-24 left-6 z-[59] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-[#F9F8F6] border border-[#F8C21C]/40 shadow-2xl flex flex-col overflow-hidden"
          >
            <div className="bg-[#143D63] text-[#F9F8F6] px-5 py-4 flex items-center gap-3">
              <div className="bg-[#F8C21C] rounded-full w-9 h-9 flex items-center justify-center">
                <Sparkles size={16} className="text-[#143D63]" />
              </div>
              <div className="flex-1">
                <p className="font-serif text-lg leading-tight">ZEBO Concierge</p>
                <p className="text-[10px] text-[#F9F8F6]/70">Usually replies in a few seconds</p>
              </div>
              <button data-testid="chat-widget-close" onClick={() => setOpen(false)} className="hover:text-[#F8C21C]"><X size={20} /></button>
            </div>

            <div ref={scrollRef} data-testid="chat-widget-messages" className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#143D63] text-[#F9F8F6] rounded-t-xl rounded-bl-xl"
                      : "bg-[#F9F8F6] text-[#143D63] border border-[#143D63]/10 rounded-t-xl rounded-br-xl"
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-[#F9F8F6] border border-[#143D63]/10 rounded-t-xl rounded-br-xl px-3.5 py-2.5 text-sm text-[#143D63]/60 inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> thinking…
                  </div>
                </div>
              )}
              {leadCreated && (
                <div className="text-center text-xs text-green-700 mt-2 inline-flex items-center gap-1 justify-center w-full">
                  <CheckCircle2 size={12} /> Lead created — our team will call within 24h.
                </div>
              )}
            </div>

            <div className="border-t border-[#143D63]/10 bg-[#F9F8F6] px-3 py-3 flex items-center gap-2">
              <textarea
                data-testid="chat-widget-input"
                rows={1}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder="Ask about services, timelines, booking…"
                className="flex-1 border border-[#143D63]/20 bg-white px-3 py-2 text-sm text-[#143D63] focus:border-[#F8C21C] focus:outline-none resize-none"
              />
              <button
                data-testid="chat-widget-send"
                onClick={send}
                disabled={sending || !input.trim()}
                className="bg-[#F8C21C] text-[#143D63] w-11 h-11 flex items-center justify-center hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors disabled:opacity-40"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
